import axios from 'axios';

// 创建axios实例
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 自动携带 JWT Token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error);
    // 401：Token 失效/未登录 → 清除本地信息并跳转登录页
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userRole');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    // 如果响应包含错误信息，将其作为error对象的一部分返回
    if (error.response) {
      // 服务器返回了错误响应
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  // 登录（必须携带滑块验证通过后的 captchaToken）
  login: (username, password, captchaToken) => api.post('/auth/login', { username, password, captchaToken }),
  // 登出
  logout: () => api.post('/auth/logout'),
  // 当前登录用户信息
  getCurrentUser: () => api.get('/auth/me'),
  // 登录页公开配置（白名单参数，免登录访问）
  getPublicConfig: () => api.get('/auth/public-config'),
};

// 滑块行为验证码 API
export const captchaAPI = {
  // 获取新挑战：{ captchaId, backgroundImage, sliderImage, sliderY, width, height, sliderSize }
  getChallenge: () => api.get('/auth/captcha/challenge'),
  // 校验滑动结果，成功返回 { success, captchaToken }
  verify: (captchaId, x, trail) => api.post('/auth/captcha/verify', { captchaId, x, trail }),
};

// 商品分类相关API
export const productCategoryAPI = {
  // 获取商品分类列表
  getProductCategories: (params) => api.get('/product-categories', { params }),
  
  // 获取单个商品分类
  getProductCategory: (id) => api.get(`/product-categories/${id}`),
  
  // 创建商品分类
  createProductCategory: (data) => api.post('/product-categories', data),
  
  // 更新商品分类
  updateProductCategory: (id, data) => api.put(`/product-categories/${id}`, data),
  
  // 删除商品分类
  deleteProductCategory: (id) => api.delete(`/product-categories/${id}`),
  
  // 搜索商品分类
  searchProductCategories: (categoryName) => api.get(`/product-categories/search?categoryName=${categoryName}`),
};

// 产品相关API
export const productAPI = {
  // 获取产品列表
  getProducts: (params) => api.get('/products', { params }),
  
  // 获取单个产品
  getProduct: (id) => api.get(`/products/${id}`),
  
  // 创建产品
  createProduct: (data) => api.post('/products', data),
  
  // 更新产品
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  
  // 删除产品
  deleteProduct: (id) => api.delete(`/products/${id}`),
  
  // 搜索产品
  searchProducts: (params) => api.get('/products/search', { params }),
  
  // 批量查重：按名称/型号精确匹配已有商品（销售订单新建商品前的重复提醒）
  checkDuplicateProducts: (candidates) => api.post('/products/check-duplicates', { candidates }),
  
  // 库存充足性检查：返回库存不足的商品（销售订单创建前提醒，不阻止下单）
  checkStock: (items) => api.post('/products/check-stock', { items }),
  
  // 获取低库存产品
  getLowStockProducts: () => api.get('/products/low-stock'),
};

// 供应商相关API
export const supplierAPI = {
  // 获取供应商列表
  getSuppliers: (params) => api.get('/suppliers', { params }),
  
  // 获取单个供应商
  getSupplier: (id) => api.get(`/suppliers/${id}`),
  
  // 创建供应商
  createSupplier: (data) => api.post('/suppliers', data),
  
  // 更新供应商
  updateSupplier: (id, data) => api.put(`/suppliers/${id}`, data),
  
  // 删除供应商
  deleteSupplier: (id) => api.delete(`/suppliers/${id}`),
  
  // 搜索供应商
  searchSuppliers: (supplierName) => api.get(`/suppliers/search?supplierName=${supplierName}`),
};

// 仓库相关API
export const warehouseAPI = {
  // 获取仓库列表
  getWarehouses: (params) => api.get('/warehouses', { params }),
  
  // 获取所有仓库（不分页）
  getAllWarehouses: () => api.get('/warehouses/all'),
  
  // 获取单个仓库
  getWarehouse: (id) => api.get(`/warehouses/${id}`),
  
  // 创建仓库
  createWarehouse: (data) => api.post('/warehouses', data),
  
  // 更新仓库
  updateWarehouse: (id, data) => api.put(`/warehouses/${id}`, data),
  
  // 删除仓库
  deleteWarehouse: (id) => api.delete(`/warehouses/${id}`),
  
  // 根据状态获取仓库
  getWarehousesByStatus: (status) => api.get(`/warehouses/status/${status}`),
  
  // 搜索仓库
  searchWarehouses: (params) => api.get('/warehouses/search', { params }),
};

// 客户相关API
export const customerAPI = {
  // 获取客户列表
  getCustomers: (params) => api.get('/customers', { params }),
  
  // 获取单个客户
  getCustomer: (id) => api.get(`/customers/${id}`),
  
  // 创建客户
  createCustomer: (data) => api.post('/customers', data),
  
  // 更新客户
  updateCustomer: (id, data) => api.put(`/customers/${id}`, data),
  
  // 删除客户
  deleteCustomer: (id) => api.delete(`/customers/${id}`),
  
  // 搜索客户
  searchCustomers: (params) => api.get('/customers/search', { params }),
  
  // 增加应收款
  addReceivable: (id, amount, remark) => api.post(`/customers/${id}/receivable/add`, null, { params: { amount, remark } }),
  
  // 减少应收款（收款）
  reduceReceivable: (id, amount, remark) => api.post(`/customers/${id}/receivable/reduce`, null, { params: { amount, remark } }),
  
  // 储值充值
  recharge: (id, amount, remark) => api.post(`/customers/${id}/prepaid/recharge`, null, { params: { amount, remark } }),
  
  // 储值扣款
  deductPrepaid: (id, amount, remark) => api.post(`/customers/${id}/prepaid/deduct`, null, { params: { amount, remark } }),
};

// 储值交易记录相关API
export const prepaidTransactionAPI = {
  // 获取储值交易记录列表
  getPrepaidTransactions: (params) => api.get('/prepaid-transactions', { params }),
  
  // 根据客户ID获取储值交易记录
  getTransactionsByCustomerId: (customerId, params) => api.get(`/prepaid-transactions/customer/${customerId}`, { params }),
  
  // 根据客户ID获取所有储值交易记录（不分页）
  getAllTransactionsByCustomerId: (customerId) => api.get(`/prepaid-transactions/customer/${customerId}/all`),
  
  // 根据交易类型获取记录
  getTransactionsByType: (transactionType, params) => api.get(`/prepaid-transactions/type/${transactionType}`, { params }),
};

// 采购订单相关API
export const purchaseOrderAPI = {
  // 获取采购订单列表
  getPurchaseOrders: (params) => api.get('/purchase-orders', { params }),
  
  // 获取单个采购订单
  getPurchaseOrder: (id) => api.get(`/purchase-orders/${id}`),
  
  // 创建采购订单
  createPurchaseOrder: (data) => api.post('/purchase-orders', data),
  
  // 更新采购订单
  updatePurchaseOrder: (id, data) => api.put(`/purchase-orders/${id}`, data),
  
  // 删除采购订单
  deletePurchaseOrder: (id) => api.delete(`/purchase-orders/${id}`),
  
  // 根据供应商获取采购订单
  getPurchaseOrdersBySupplier: (supplierId) => api.get(`/purchase-orders/supplier/${supplierId}`),
  
  // 根据状态获取采贯订单
  getPurchaseOrdersByStatus: (status) => api.get(`/purchase-orders/status/${status}`),
  
  // 取消订单
  cancelPurchaseOrder: (id) => api.post(`/purchase-orders/${id}/cancel`),

  // 查询商品关联的采购订单
  getPurchaseOrdersByProduct: (productId, params) => api.get(`/purchase-orders/by-product/${productId}`, { params }),

  // ===== 订单回收站 =====
  getPurchaseRecycleBin: (params) => api.get('/purchase-orders/recycle-bin', { params }),
  restorePurchaseOrder: (id) => api.post(`/purchase-orders/${id}/restore`),
  permanentDeletePurchaseOrder: (id) => api.delete(`/purchase-orders/recycle-bin/${id}`),
  clearPurchaseRecycleBin: () => api.delete('/purchase-orders/recycle-bin/clear'),
};

// 采购退货相关API
export const purchaseReturnAPI = {
  // 获取采购退货单列表
  getPurchaseReturns: (params) => api.get('/purchase-returns', { params }),
  
  // 获取单个采购退货单
  getPurchaseReturn: (id) => api.get(`/purchase-returns/${id}`),
  
  // 创建采购退货单
  createPurchaseReturn: (data) => api.post('/purchase-returns', data),
  
  // 更新采购退货单
  updatePurchaseReturn: (id, data) => api.put(`/purchase-returns/${id}`, data),
  
  // 删除采购退货单
  deletePurchaseReturn: (id) => api.delete(`/purchase-returns/${id}`),
  
  // 根据采购订单获取退货单
  getPurchaseReturnsByOrderId: (purchaseOrderId) => api.get(`/purchase-returns/by-purchase-order/${purchaseOrderId}`),
  
  // 根据供应商获取退货单
  getPurchaseReturnsBySupplier: (supplierId) => api.get(`/purchase-returns/by-supplier/${supplierId}`),
  
  // 根据状态获取退货单
  getPurchaseReturnsByStatus: (status) => api.get(`/purchase-returns/status/${status}`),
};

// 销售订单相关API
export const salesOrderAPI = {
  // 获取销售订单列表
  getSalesOrders: (params) => api.get('/sales-orders', { params }),
  
  // 获取单个销售订单
  getSalesOrder: (id) => api.get(`/sales-orders/${id}`),
  
  // 创建销售订单
  createSalesOrder: (data) => api.post('/sales-orders', data),
  
  // 更新销售订单
  updateSalesOrder: (id, data) => api.put(`/sales-orders/${id}`, data),
  
  // 删除销售订单
  deleteSalesOrder: (id) => api.delete(`/sales-orders/${id}`),
  
  // 根据客户获取销售订单
  getSalesOrdersByCustomer: (customerId) => api.get(`/sales-orders/customer/${customerId}`),
  
  // 根据状态获取销售订单
  getSalesOrdersByStatus: (status) => api.get(`/sales-orders/status/${status}`),
  
  // 更新订单状态
  updateSalesOrderStatus: (id, status) => api.put(`/sales-orders/${id}/status`, { status }),
  
  // 更新发货状态
  updateSalesOrderDeliveryStatus: (id, deliveryStatus) => api.put(`/sales-orders/${id}/delivery-status`, { deliveryStatus }),
  
  // 订单发货
  deliverSalesOrder: (id, deliveryData) => api.post(`/sales-orders/${id}/deliver`, deliveryData),
  
  // 处理订单支付（可传入用户选择的结算方式）
  processPayment: (id, settlementMethod) => api.post(`/sales-orders/${id}/process-payment`, null, {
    params: settlementMethod ? { settlementMethod } : undefined
  }),
  
  // 取消订单
  cancelSalesOrder: (id) => api.post(`/sales-orders/${id}/cancel`),

  // 查询商品关联的销售订单
  getSalesOrdersByProduct: (productId, params) => api.get(`/sales-orders/by-product/${productId}`, { params }),

  // ===== 订单回收站 =====
  getSalesRecycleBin: (params) => api.get('/sales-orders/recycle-bin', { params }),
  restoreSalesOrder: (id) => api.post(`/sales-orders/${id}/restore`),
  permanentDeleteSalesOrder: (id) => api.delete(`/sales-orders/recycle-bin/${id}`),
  clearSalesRecycleBin: () => api.delete('/sales-orders/recycle-bin/clear'),
};

// 库存相关API
export const inventoryAPI = {
  // 获取库存列表
  getInventories: (params) => api.get('/inventories', { params }),
  
  // 获取单个库存
  getInventory: (id) => api.get(`/inventories/${id}`),
  
  // 查询负库存记录（总库存或可用库存为负，用于重要提醒）
  getNegativeStock: () => api.get('/inventories/negative-stock'),
  
  // 创建库存
  createInventory: (data) => api.post('/inventories', data),
  
  // 更新库存
  updateInventory: (id, data) => api.put(`/inventories/${id}`, data),
  
  // 删除库存
  deleteInventory: (id) => api.delete(`/inventories/${id}`),
  
  // 根据商品获取库存
  getInventoriesByProduct: (productId) => api.get(`/inventories/product/${productId}`),
  
  // 根据仓库获取库存
  getInventoriesByWarehouse: (warehouseId) => api.get(`/inventories/warehouse/${warehouseId}`),
  
  // 根据商品和仓库获取库存
  getInventoryByProductAndWarehouse: (productId, warehouseId) => 
    api.get(`/inventories/product/${productId}/warehouse/${warehouseId}`),
  
  // 更新库存数量
  updateQuantity: (productId, warehouseId, quantity) => 
    api.put(`/inventories/product/${productId}/warehouse/${warehouseId}/quantity/${quantity}`),
  
  // 增加库存数量
  increaseQuantity: (productId, warehouseId, quantity) => 
    api.put(`/inventories/product/${productId}/warehouse/${warehouseId}/increase/${quantity}`),
  
  // 减少库存数量
  decreaseQuantity: (productId, warehouseId, quantity) => 
    api.put(`/inventories/product/${productId}/warehouse/${warehouseId}/decrease/${quantity}`),
};

// 报表相关API
export const reportAPI = {
  // 商品销售统计
  getProductSalesReport: (productId, startDate, endDate) => 
    api.get(`/reports/product-sales?productId=${productId}&startDate=${startDate}&endDate=${endDate}`),
  
  // 库存统计
  getInventoryReport: () => api.get('/reports/inventory'),
  
  // 采购统计
  getPurchaseReport: (startDate, endDate) => 
    api.get(`/reports/purchase?startDate=${startDate}&endDate=${endDate}`),
  
  // 销售统计
  getSalesReport: (startDate, endDate) => 
    api.get(`/reports/sales?startDate=${startDate}&endDate=${endDate}`),
  
  // 利润统计
  getProfitReport: (startDate, endDate) => 
    api.get(`/reports/profit?startDate=${startDate}&endDate=${endDate}`),
  
  // 供应商统计
  getSupplierReport: () => api.get('/reports/supplier'),
  
  // 供应商采购明细统计
  getSupplierPurchaseReport: (supplierId, startDate, endDate) => {
    let url = `/reports/supplier-purchase?startDate=${startDate}&endDate=${endDate}`;
    if (supplierId) {
      url += `&supplierId=${supplierId}`;
    }
    return api.get(url);
  },
  
  // 客户统计
  getCustomerReport: (customerId) => {
    const url = customerId ? `/reports/customer?customerId=${customerId}` : '/reports/customer';
    return api.get(url);
  },
  
  // 低库存预警
  getLowStockWarning: () => api.get('/reports/low-stock-warning'),
  
  // 商品销售排行榜
  getProductSalesRanking: (startDate, endDate, limit) => 
    api.get(`/reports/product-sales-ranking?startDate=${startDate}&endDate=${endDate}&limit=${limit}`),
  
  // 采购订单统计
  getPurchaseOrderReport: (startDate, endDate) => 
    api.get(`/reports/purchase-orders?startDate=${startDate}&endDate=${endDate}`),
  
  // 销售订单统计
  getSalesOrderReport: (startDate, endDate) => 
    api.get(`/reports/sales-orders?startDate=${startDate}&endDate=${endDate}`),
  
  // 库存周转率统计
  getInventoryTurnoverReport: (startDate, endDate) => 
    api.get(`/reports/inventory-turnover?startDate=${startDate}&endDate=${endDate}`),
  
  // 应收账目统计
  getReceivableReport: (startDate, endDate, settlementMethod) => {
    let url = `/reports/receivable?startDate=${startDate}&endDate=${endDate}`;
    if (settlementMethod) {
      url += `&settlementMethod=${settlementMethod}`;
    }
    return api.get(url);
  },
  
  // 应付账目统计
  getPayableReport: (startDate, endDate, settlementStatus) => {
    let url = `/reports/payable?startDate=${startDate}&endDate=${endDate}`;
    if (settlementStatus) {
      url += `&settlementStatus=${settlementStatus}`;
    }
    return api.get(url);
  },

  // 订单进度统计（看板用）
  getOrderProgress: () => api.get('/reports/order-progress'),

  // 销售统计报告（多条件查询）
  getSalesStatisticsReport: (params) => api.get('/reports/sales-statistics', { params }),

  // 库存盘点报表（多条件筛选）
  getInventoryCheckReport: (params) => api.get('/reports/inventory-check', { params }),
};

// 营业员相关API
export const staffAPI = {
  // 获取营业员列表
  getStaffs: (params) => api.get('/staff', { params }),
  
  // 获取单个营业员
  getStaff: (id) => api.get(`/staff/${id}`),
  
  // 创建营业员
  createStaff: (data) => api.post('/staff', data),
  
  // 更新营业员
  updateStaff: (id, data) => api.put(`/staff/${id}`, data),
  
  // 管理员重置密码
  resetPassword: (id, password) => api.put(`/staff/${id}/password`, { password }),
  
  // 删除营业员
  deleteStaff: (id) => api.delete(`/staff/${id}`),
  
  // 根据用户名获取营业员
  getStaffByUsername: (username) => api.get(`/staff/username/${username}`),
  
  // 根据员工编码获取营业员
  getStaffByCode: (staffCode) => api.get(`/staff/code/${staffCode}`),
  
  // 搜索营业员
  searchStaffs: (realName) => api.get(`/staff/search?realName=${realName}`),
  
  // 更新头像
  updateAvatar: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/staff/${id}/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // 显式设置为multipart/form-data
      }
    });
  },
  
  // 获取头像
  getAvatar: (id) => api.get(`/staff/${id}/avatar`),
};

// 系统参数相关API
export const systemParamAPI = {
  // 获取系统参数列表
  getSystemParams: (params) => api.get('/system-params', { params }),
  
  // 获取单个系统参数
  getSystemParam: (id) => api.get(`/system-params/${id}`),
  
  // 创建系统参数
  createSystemParam: (data) => api.post('/system-params', data),
  
  // 更新系统参数
  updateSystemParam: (id, data) => api.put(`/system-params/${id}`, data),
  
  // 删除系统参数
  deleteSystemParam: (id) => api.delete(`/system-params/${id}`),
  
  // 根据参数键获取系统参数
  getSystemParamByKey: (paramKey) => api.get(`/system-params/key/${paramKey}`),
  
  // 根据分类获取系统参数
  getSystemParamsByCategory: (category) => api.get(`/system-params/category/${category}`),
  
  // 根据状态获取系统参数
  getSystemParamsByStatus: (status) => api.get(`/system-params/status/${status}`),
  
  // 获取参数值
  getParamValue: (paramKey) => api.get(`/system-params/value/${paramKey}`),
  
  // 更新参数值
  updateParamValue: (paramKey, paramValue) => 
    api.put(`/system-params/value/${paramKey}`, { paramValue }),

  // ==================== 短信通知 ====================
  // 查询短信配置完整性状态（服务商/密钥/签名/模板，不含密钥明文）
  getSmsStatus: () => api.get('/system-params/sms-status'),

  // 发送测试短信（复用新工单模板 + 样例变量）
  testSms: (phone) => api.post('/system-params/test-sms', { phone }),
};



// 系统维护相关API
export const maintenanceAPI = {
  // 获取系统环境信息
  getEnvironmentInfo: () => api.get('/maintenance/environment'),
  
  // 获取数据库配置信息
  getDatabaseConfig: () => api.get('/maintenance/database'),
  
  // 数据库备份
  backupDatabase: () => api.post('/maintenance/backup', null, { timeout: 300000 }),
  
  // 备份并发送邮件
  backupAndEmail: (toEmail) => api.post('/maintenance/backup-email', null, {
    params: toEmail ? { toEmail } : {},
    timeout: 360000,
  }),
  
  // 测试邮件配置
  testEmail: (toEmail) => api.post('/maintenance/email-test', null, { params: { toEmail }, timeout: 30000 }),
  
  // 获取备份列表
  getBackupList: () => api.get('/maintenance/backup/list'),
  
  // 删除备份文件
  deleteBackup: (fileName) => api.delete('/maintenance/backup/delete', { params: { file: fileName } }),
  
  // 下载备份文件
  downloadBackup: (fileName) => {
    return api.get('/maintenance/backup/download', {
      params: { file: fileName },
      responseType: 'blob',
      timeout: 300000,
    });
  },
  
  // 数据库恢复（上传本地 SQL 文件）
  restoreDatabase: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/maintenance/restore', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000,
    });
  },

  // 数据库恢复（直接使用服务器备份目录内的文件）
  restoreFromBackupFile: (fileName) => api.post('/maintenance/restore-from-file', null, {
    params: { file: fileName },
    timeout: 600000,
  }),

  // 获取自动备份计划状态
  getScheduleStatus: () => api.get('/maintenance/schedule/status'),
};

// 资金流水相关API
// 资金流水类别管理
export const cashFlowCategoryAPI = {
  getCategories: (params) => api.get('/cash-flow-categories', { params }),
  createCategory: (data) => api.post('/cash-flow-categories', data),
  updateCategory: (id, data) => api.put(`/cash-flow-categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/cash-flow-categories/${id}`),
};

export const cashFlowAPI = {
  // 获取资金流水列表
  getCashFlows: (params) => api.get('/cash-flows', { params }),
  
  // 获取单个资金流水
  getCashFlow: (id) => api.get(`/cash-flows/${id}`),
  
  // 创建资金流水
  createCashFlow: (data) => api.post('/cash-flows', data),
  
  // 更新资金流水
  updateCashFlow: (id, data) => api.put(`/cash-flows/${id}`, data),
  
  // 删除资金流水
  deleteCashFlow: (id) => api.delete(`/cash-flows/${id}`),
  
  // 获取统计数据
  getStatistics: (params) => api.get('/cash-flows/statistics', { params }),
  
  // 导出资金流水
  exportCashFlows: (params) => {
    const queryString = new URLSearchParams(params).toString();
    window.location.href = `/api/cash-flows/export?${queryString}`;
  },
};

// 采购发票相关API
export const purchaseInvoiceAPI = {
  // 获取发票列表
  getInvoices: (params) => api.get('/purchase-invoices', { params }),
  
  // 获取单个发票
  getInvoice: (id) => api.get(`/purchase-invoices/${id}`),
  
  // 根据订单ID获取发票
  getInvoicesByOrderId: (orderId) => api.get(`/purchase-invoices/order/${orderId}`),
  
  // 保存/更新发票
  saveInvoice: (data) => api.post('/purchase-invoices', data),
  
  // 删除发票
  deleteInvoice: (id) => api.delete(`/purchase-invoices/${id}`),
  
  // 获取发票超时跟踪列表
  getOverdueTracking: () => api.get('/purchase-invoices/overdue-tracking'),

  // 上传发票 PDF 自动识别（提取文本层解析出结构化字段）
  parseInvoicePdf: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/purchase-invoices/parse-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
};

// 部门管理API
export const departmentAPI = {
  // 获取部门列表
  getDepartments: () => api.get('/departments'),
  
  // 获取单个部门
  getDepartment: (id) => api.get(`/departments/${id}`),
  
  // 创建部门
  createDepartment: (data) => api.post('/departments', data),
  
  // 更新部门
  updateDepartment: (id, data) => api.put(`/departments/${id}`, data),
  
  // 删除部门
  deleteDepartment: (id) => api.delete(`/departments/${id}`),
};

// 职位管理API
export const positionAPI = {
  // 获取职位列表
  getPositions: () => api.get('/positions'),
  
  // 获取单个职位
  getPosition: (id) => api.get(`/positions/${id}`),
  
  // 创建职位
  createPosition: (data) => api.post('/positions', data),
  
  // 更新职位
  updatePosition: (id, data) => api.put(`/positions/${id}`, data),
  
  // 删除职位
  deletePosition: (id) => api.delete(`/positions/${id}`),
};

// 附件相关API
export const attachmentAPI = {
  // 获取附件列表
  getAttachments: (bizType, bizId) =>
    api.get('/attachments', { params: { bizType, bizId } }),

  // 上传附件
  uploadAttachment: (bizType, bizId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/attachments/upload?bizType=${bizType}&bizId=${bizId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  // 删除附件
  deleteAttachment: (id) => api.delete(`/attachments/${id}`),

  // 预览 URL
  getPreviewUrl: (id) => `/api/attachments/${id}/preview`,

  // 下载 URL
  getDownloadUrl: (id) => `/api/attachments/${id}/download`,
};

// 商品品牌 API
export const brandAPI = {
  // 品牌CRUD
  getBrands: (params) => api.get('/brands', { params }),
  getBrand: (id) => api.get(`/brands/${id}`),
  createBrand: (data) => api.post('/brands', data),
  updateBrand: (id, data) => api.put(`/brands/${id}`, data),
  deleteBrand: (id) => api.delete(`/brands/${id}`),
  searchBrands: (brandName) => api.get('/brands/search', { params: { brandName } }),

  // 供应商-品牌关联
  getSuppliersByBrand: (brandId) => api.get(`/brands/${brandId}/suppliers`),
  getBrandsBySupplier: (supplierId) => api.get(`/brands/supplier/${supplierId}`),
  updateSupplierBrands: (supplierId, brandIds) => api.put(`/brands/supplier/${supplierId}`, { brandIds }),
};

// 系统日志 API
export const systemLogAPI = {
  getLoginLogs: (params) => api.get('/system-logs/login', { params }),
  getOperationLogs: (params) => api.get('/system-logs/operation', { params }),
  cleanLoginLogs: (keepDays) => api.delete('/system-logs/login/clean', { params: { keepDays } }),
  cleanOperationLogs: (keepDays) => api.delete('/system-logs/operation/clean', { params: { keepDays } }),
};

// ============================================================
// 客户自助维修门户（独立登录态 portal_token）
// ============================================================
const portalHttp = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

portalHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

portalHttp.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_customer');
      if (!window.location.pathname.startsWith('/portal/login')) {
        window.location.href = '/portal/login';
      }
    }
    return Promise.reject(error);
  }
);

// 门户认证与工单 API
export const portalAPI = {
  register: (data) => portalHttp.post('/portal/auth/register', data),
  login: (phone, password) => portalHttp.post('/portal/auth/login', { phone, password }),
  getProfile: () => portalHttp.get('/portal/auth/profile'),
  updateProfile: (data) => portalHttp.put('/portal/auth/profile', data),
  changePassword: (oldPassword, newPassword) => portalHttp.post('/portal/auth/password', { oldPassword, newPassword }),

  // 首页工作台（统计 / 待办 / 最近工单 / 结算概览）
  getHome: () => portalHttp.get('/portal/repair-orders/home'),

  // 维修申请
  submitOrder: (data) => portalHttp.post('/portal/repair-orders', data),
  getOrders: (params) => portalHttp.get('/portal/repair-orders', { params }),
  getOrder: (id) => portalHttp.get(`/portal/repair-orders/${id}`),
  resubmitOrder: (id, data) => portalHttp.put(`/portal/repair-orders/${id}`, data),
  withdrawOrder: (id) => portalHttp.post(`/portal/repair-orders/${id}/withdraw`),
  cancelOrder: (id, reason) => portalHttp.post(`/portal/repair-orders/${id}/cancel`, { reason }),

  // 报价
  getQuote: (id) => portalHttp.get(`/portal/repair-orders/${id}/quote`),
  confirmQuote: (id) => portalHttp.post(`/portal/repair-orders/${id}/quote/confirm`),
  rejectQuote: (id, reason) => portalHttp.post(`/portal/repair-orders/${id}/quote/reject`, { reason }),

  // 服务单 / 轨迹 / 进度 / 验收 / 结算
  getService: (id) => portalHttp.get(`/portal/repair-orders/${id}/service`),
  getLogs: (id) => portalHttp.get(`/portal/repair-orders/${id}/logs`),
  getProgress: (id) => portalHttp.get(`/portal/repair-orders/${id}/progress`),
  acceptOrder: (id, data) => portalHttp.post(`/portal/repair-orders/${id}/acceptance`, data),
  getSettlement: (id) => portalHttp.get(`/portal/repair-orders/${id}/settlement`),

  // 附件（故障素材 / 现场照片）
  getAttachments: (bizId) => portalHttp.get('/attachments', { params: { bizType: 'repair_order', bizId } }),
  uploadAttachment: (bizId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return portalHttp.post(`/attachments/upload?bizType=repair_order&bizId=${bizId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  deleteAttachment: (id) => portalHttp.delete(`/attachments/${id}`),
  // 预览需携带门户令牌，返回 blob
  getAttachmentBlob: (id) => portalHttp.get(`/attachments/${id}/preview`, { responseType: 'blob' }),
};

// 维修工单后台管理 API
export const repairAPI = {
  getOrders: (params) => api.get('/repair/orders', { params }),
  getOrder: (id) => api.get(`/repair/orders/${id}`),
  getStatusOptions: () => api.get('/repair/orders/status-options'),

  audit: (id, pass, reason) => api.post(`/repair/orders/${id}/audit`, { pass, reason }),

  saveQuote: (id, data) => api.post(`/repair/orders/${id}/quote`, data),
  submitQuote: (id, data) => api.post(`/repair/orders/${id}/quote/submit`, data),
  updateQuote: (id, data) => api.put(`/repair/orders/${id}/quote`, data),

  createServiceOrder: (id, data) => api.post(`/repair/orders/${id}/service`, data),
  updateStatus: (id, status, remark) => api.post(`/repair/orders/${id}/status`, { status, remark }),
  addProgress: (id, content) => api.post(`/repair/orders/${id}/progress`, { content }),
  complete: (id, content) => api.post(`/repair/orders/${id}/complete`, { content }),
  finish: (id) => api.post(`/repair/orders/${id}/finish`),

  getSettlements: (params) => api.get('/repair/settlements', { params }),
  updateSettlement: (id, data) => api.put(`/repair/settlements/${id}`, data),
  getStats: () => api.get('/repair/stats'),
  // 导出需携带令牌，返回 blob
  exportSettlements: (params) => api.get('/repair/settlements/export', { params, responseType: 'blob' }),

  // 维修门户客户管理
  getCustomers: (params) => api.get('/repair/customers', { params }),
  getCustomer: (id) => api.get(`/repair/customers/${id}`),
  createCustomer: (data) => api.post('/repair/customers', data),
  updateCustomer: (id, data) => api.put(`/repair/customers/${id}`, data),
  setCustomerStatus: (id, status) => api.put(`/repair/customers/${id}/status`, { status }),
  resetCustomerPassword: (id, password) => api.put(`/repair/customers/${id}/password`, { password }),
};

export default api;